/**
 * What the sheet workspace needs beyond the API (ADR-0020): which configuration comes
 * next, how one looks, and how to undo an operation. Undo is another operation: the one
 * that puts back what the first changed, worked out from the sheet before and after.
 * Some operations have no exact inverse (grouping, approval); those cannot be undone
 * here and say so.
 */
import type { Workspace, WorkspaceConfig, WorkspaceOp } from "@/api/client";

export type Box = { x0: number; y0: number; x1: number; y1: number };

/** Configurations the estimator works through: not deleted, in reading order. */
export function liveConfigs(ws: Workspace): WorkspaceConfig[] {
  return ws.configurations.filter((c) => !c.deleted).sort((a, b) => a.position - b.position);
}

/** The configuration `step` places from `id` (wrapping), or the first when none is chosen. */
export function stepFrom(ws: Workspace, id: string | null, step: number): string | null {
  const live = liveConfigs(ws);
  if (!live.length) {
    return null;
  }
  const at = live.findIndex((c) => c.id === id);
  if (at < 0) {
    return live[step < 0 ? live.length - 1 : 0]!.id;
  }
  return live[(at + step + live.length) % live.length]!.id;
}

export type Look = "approved" | "flagged" | "proposed";

export function lookOf(c: WorkspaceConfig): Look {
  if (c.status === "approved") {
    return "approved";
  }
  return c.flags.length ? "flagged" : "proposed";
}

/** The first configuration still to approve, after `id` in reading order, wrapping. */
export function nextToReview(ws: Workspace, id: string | null): string | null {
  const live = liveConfigs(ws);
  const at = Math.max(
    0,
    live.findIndex((c) => c.id === id),
  );
  for (let k = 1; k <= live.length; k++) {
    const c = live[(at + k) % live.length]!;
    if (c.status !== "approved") {
      return c.id;
    }
  }
  return null;
}

function config(ws: Workspace, id: string): WorkspaceConfig | undefined {
  return ws.configurations.find((c) => c.id === id);
}

function pane(ws: Workspace, id: string) {
  for (const c of ws.configurations) {
    const found = c.panes.find((p) => p.id === id);
    if (found) {
      return found;
    }
  }
  return undefined;
}

function created(before: Workspace, after: Workspace): string | undefined {
  const known = new Set(before.configurations.flatMap((c) => [c.id, ...c.panes.map((p) => p.id)]));
  for (const c of after.configurations) {
    if (!known.has(c.id)) return c.id;
    for (const p of c.panes) if (!known.has(p.id)) return p.id;
  }
  return undefined;
}

const same = (a: number, b: number) => Math.abs(a - b) < 1e-6;

/**
 * The operation that undoes `op`, given the sheet before and after it; null when it
 * cannot be undone by another operation.
 */
export function inverseOf(
  op: WorkspaceOp,
  before: Workspace,
  after: Workspace,
): WorkspaceOp | null {
  switch (op.op) {
    case "move_config": {
      const was = config(before, op.configuration_id);
      return was ? { op: "move_config", configuration_id: was.id, box: was.bbox_norm } : null;
    }
    case "delete_config":
      return { op: "restore_config", configuration_id: op.configuration_id };
    case "restore_config":
      return { op: "delete_config", configuration_id: op.configuration_id };
    case "label_config": {
      const was = config(before, op.configuration_id);
      return was ? { op: "label_config", configuration_id: was.id, label: was.label } : null;
    }
    case "add_break":
      return { op: "remove_break", configuration_id: op.configuration_id, y: op.y };
    case "remove_break":
      return { op: "add_break", configuration_id: op.configuration_id, y: op.y };
    case "add_vline":
      return { op: "remove_vline", configuration_id: op.configuration_id, x: op.x };
    case "remove_vline": {
      const line = config(before, op.configuration_id)?.vlines.find((v) => same(v.x, op.x));
      return line
        ? {
            op: "add_vline",
            configuration_id: op.configuration_id,
            x: line.x,
            y0: line.y0,
            y1: line.y1,
          }
        : null;
    }
    case "set_typical": {
      const was = config(before, op.configuration_id)?.typical.find((t) => t.row === op.row);
      return {
        op: "set_typical",
        configuration_id: op.configuration_id,
        row: op.row,
        count: was?.count ?? 1,
      };
    }
    case "add_config": {
      const id = created(before, after);
      return id ? { op: "delete_config", configuration_id: id } : null;
    }
    case "add_pane": {
      const id = created(before, after);
      return id ? { op: "delete_pane", pane_id: id } : null;
    }
    case "edit_pane": {
      const was = pane(before, op.pane_id);
      return was
        ? {
            op: "edit_pane",
            pane_id: was.id,
            box: was.bbox_norm,
            kind: was.kind as "pane",
            tags: was.tags,
          }
        : null;
    }
    case "delete_pane":
      return { op: "restore_pane", pane_id: op.pane_id };
    case "restore_pane":
      return { op: "delete_pane", pane_id: op.pane_id };
    case "name_unit": {
      const key = [...op.pane_ids].sort().join(",");
      const was = config(before, op.configuration_id)?.units.find((u) => u.key === key);
      return {
        op: "name_unit",
        configuration_id: op.configuration_id,
        pane_ids: op.pane_ids,
        name: was?.edited ? was.name : null,
      };
    }
    case "note": {
      const was = config(before, op.configuration_id);
      return was ? { op: "note", configuration_id: was.id, text: was.note } : null;
    }
    default:
      return null;
  }
}

/** A box drawn by dragging, in either direction, as a page-normalised box. */
export function boxFrom(a: [number, number], b: [number, number]): Box {
  return {
    x0: Math.max(0, Math.min(a[0], b[0])),
    y0: Math.max(0, Math.min(a[1], b[1])),
    x1: Math.min(1, Math.max(a[0], b[0])),
    y1: Math.min(1, Math.max(a[1], b[1])),
  };
}

/** A box big enough to be meant, not a click. */
export function isDrawn(b: Box): boolean {
  return b.x1 - b.x0 > 0.002 && b.y1 - b.y0 > 0.002;
}

/** Inches as feet and inches to a quarter: 46.75 is `3'-10 3/4"`. */
export function feetInches(inches: number | null | undefined): string {
  if (inches === null || inches === undefined) {
    return "";
  }
  const quarters = Math.round(inches * 4);
  const feet = Math.floor(quarters / 48);
  const rest = quarters - feet * 48;
  const whole = Math.floor(rest / 4);
  const part = ["", " 1/4", " 1/2", " 3/4"][rest % 4];
  return `${feet}'-${whole}${part}"`;
}

/** The unit a pane belongs to. */
export function unitOf(c: WorkspaceConfig, paneId: string) {
  return c.units.find((u) => u.pane_ids.includes(paneId));
}

/** The pane `step` places from `id` among a configuration's panes, wrapping. */
export function stepPane(c: WorkspaceConfig, id: string | null, step: number): string | null {
  if (!c.panes.length) {
    return null;
  }
  const at = c.panes.findIndex((p) => p.id === id);
  if (at < 0) {
    return c.panes[step < 0 ? c.panes.length - 1 : 0]!.id;
  }
  return c.panes[(at + step + c.panes.length) % c.panes.length]!.id;
}

export type PaneKind = "pane" | "leaf" | "door" | "shape";

/** D makes a pane a door and a door a pane again; V does the same for a vent (a leaf). */
export function toggled(kind: string, to: "door" | "leaf"): PaneKind {
  return kind === to ? "pane" : to;
}

/** Tags as typed: `GL-1, mp-2 ` is `["GL-1", "MP-2"]`. */
export function parseTags(text: string): string[] {
  return text
    .split(/[\s,]+/)
    .map((t) => t.trim().toUpperCase())
    .filter(Boolean);
}

/** The panes chosen are exactly one unit's, so naming them names that unit. */
export function chosenUnit(c: WorkspaceConfig, paneIds: readonly string[]) {
  const key = [...paneIds].sort().join(",");
  return c.units.find((u) => u.key === key);
}
